import {
  getMacroSplitPreset,
  normalizeNutritionMacroSplit,
  toCaloriePercentageTargets,
  updateNutritionMacroSplit,
} from "@fitness-app/data-models/ai/nutrition-macro-split";
import { getDefaultAiModelId, resolveAiModelId } from "@fitness-app/data-models/ai/tasks";
import { NutritionGenerationCaloriesField } from "./NutritionGenerationCaloriesField";
import { Checkbox, Col, Form, Input, InputNumber, Row, Select, Slider, Switch, Typography } from "antd";
import { type FormInstance } from "antd/lib/form";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { type ClientMealsPlanDetails } from "@fitness-app/data-models/entities/ClientNutrition";
import {
  DietaryPreference,
  KCAL_PER_NUTRIENT_GRAM,
  MacroSplit,
  MealsPerDay,
  TargetGender,
} from "@fitness-app/data-models/entities/MealsPlan";
import { countNutrientGrams } from "@fitness-app/utils/src/nutrition/countNutrients";
import {
  type NutrientsTargets,
  type TraineeNutritionGeneratorFormModel,
} from "@fitness-app/utils/src/nutrition/mealsPlanGenerators";

import { useMealPlanOptions } from "~/modules/Nutrition/hooks/useMealPlanOptions";
import { useMealsPlanContextNullable } from "~/shared/providers/MealsPlanProvider";
import { analyticsService, useAppSelector } from "~/store/initializeStore";

interface TraineeNutritionFormProps {
  formController?: FormInstance<TraineeNutritionGeneratorFormModel>;
  onSubmit: (
    formData: TraineeNutritionGeneratorFormModel,
    targets: NutrientsTargets,
    supplements?: ClientMealsPlanDetails["supplements"],
  ) => void;
  model?: Partial<TraineeNutritionGeneratorFormModel> | null;
  traineeId: string;
}

const IS_PRODUCTION = import.meta.env.VITE_APP_ENV === "production";

const TraineeNutritionGeneratorForm = React.memo(({ model, onSubmit, formController }: TraineeNutritionFormProps) => {
  const { t } = useTranslation(["nutrition", "common"]);
  const { dietaryPreferences, nutritionExclusions, mealsPerDay, macroSplits, models } = useMealPlanOptions();
  const activeMealsPlanId = useAppSelector((store) => store.trainee.profile?.activeMealsPlanId);
  const context = useMealsPlanContextNullable();
  const [macroTargets, setMacroTargets] = useState(() =>
    toCaloriePercentageTargets(model?.macroSplit ?? MacroSplit.LOW_CARB, model?.targets),
  );
  const { protein, carbs, fat } = macroTargets;
  const targetCalories = Form.useWatch(["targetCalories"], formController);
  const generateEmptyTemplate = Form.useWatch(["generateEmptyTemplate"], formController);
  const [submitCount, setSubmitCount] = useState(0);

  useEffect(() => {
    if (model && submitCount === 0) {
      // a re-run prefill may carry an older version; in production the field is hidden so it must stay v3
      formController?.setFieldsValue(IS_PRODUCTION ? { ...model, generatorVersion: "v3" } : model);
      setMacroTargets(toCaloriePercentageTargets(model.macroSplit, model.targets));
    }
  }, [model, submitCount, formController]);

  // the preset applies only when the trainer picks a split; watching the field would overwrite prefilled targets
  const onMacroSplitChange = (value: MacroSplit) =>
    setMacroTargets(normalizeNutritionMacroSplit(getMacroSplitPreset(value)));

  const update = (type: "protein" | "carbs" | "fat", value: number | null) => {
    if (value !== null) setMacroTargets((current) => updateNutritionMacroSplit(current, type, value));
  };

  const handleSubmit = useCallback(
    (model: TraineeNutritionGeneratorFormModel) => {
      setSubmitCount((prev) => prev + 1);
      if (!model.generateEmptyTemplate) {
        analyticsService.track("nutrition_plan_generation_started", {
          targetCalories: model.targetCalories,
          dietaryPreference: model.dietaryPreference,
          mealsSchema: model.mealsSchema,
          llmModel: model.model,
          macroSplit: model.macroSplit,
        });
      }
      onSubmit(
        model.generateEmptyTemplate
          ? {
              ...model,
              prompt: "",
              generatorVersion: "v3",
              model: resolveAiModelId("nutrition", model.model),
            }
          : model,
        {
          carbs,
          protein,
          fat,
        },
        context?.planDetails.supplements || [],
      );
    },
    [onSubmit, carbs, protein, fat, context?.planDetails.supplements],
  );

  return (
    <>
      <Form<TraineeNutritionGeneratorFormModel>
        name="form"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 14 }}
        layout="horizontal"
        form={formController}
        initialValues={{
          targetCalories: 1800,
          dietaryPreference: DietaryPreference.NoPreference,
          mealsSchema: MealsPerDay.THREE,
          macroSplit: MacroSplit.LOW_CARB,
          targets: {
            gender: TargetGender.UNISEX,
          },
          copySupplementsPlan: true,
          prompt: "",
          generateEmptyTemplate: false,
          generatorVersion: "v3",
          model: getDefaultAiModelId("nutrition"),
          generateDishImages: false,
        }}
        onFinish={handleSubmit}
      >
        <Form.Item
          name="name"
          label={t<string>("mealPlanForm.name")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Input autoFocus />
        </Form.Item>

        <Form.Item name="generateEmptyTemplate" valuePropName="checked" wrapperCol={{ offset: 8, span: 14 }}>
          <Checkbox>Generuj pusty szablon</Checkbox>
        </Form.Item>

        {!generateEmptyTemplate && (
          <>
            <Form.Item
              name="prompt"
              label={t<string>("mealPlanForm.prompt")}
              rules={[
                {
                  required: false,
                  message: t<string>("common:validationErrors.fieldIsRequired"),
                },
              ]}
            >
              <Input.TextArea rows={4} />
            </Form.Item>

            {/* hidden in production: trainers always run v3 there, the value still submits */}
            <Form.Item
              name="generatorVersion"
              label="Wersja generatora"
              hidden={IS_PRODUCTION}
              rules={[
                {
                  required: true,
                  message: t<string>("common:validationErrors.fieldIsRequired"),
                },
              ]}
            >
              <Select
                options={[
                  { value: "v1", label: "Obecny generator" },
                  { value: "v2", label: "Generator V2 - historyczne diety + walidacja" },
                  { value: "v3", label: "Generator V3 - makro + wykluczenia + skalowanie porcji" },
                ]}
                placeholder={t("common:selectPlaceholder")}
              />
            </Form.Item>

            <Form.Item
              name="model"
              label={t<string>("mealPlanForm.model")}
              rules={[
                {
                  required: true,
                  message: t<string>("common:validationErrors.fieldIsRequired"),
                },
              ]}
            >
              <Select options={models} placeholder={t("common:selectPlaceholder")} />
            </Form.Item>

            <Form.Item
              name="generateDishImages"
              label="Zdjęcia potraw"
              valuePropName="checked"
              tooltip="Po wygenerowaniu jadłospisu automatycznie powstaną zdjęcia potraw. Dotyczy generatora V3."
            >
              <Switch />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="dietaryPreference"
          label={t<string>("mealPlanForm.dietaryPreference")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Select options={dietaryPreferences} placeholder={t("common:selectPlaceholder")} />
        </Form.Item>
        <Form.Item name="exclusions" label={t<string>("mealPlanForm.exclusions")}>
          <Select mode="multiple" options={nutritionExclusions} placeholder={t("common:selectPlaceholder")} />
        </Form.Item>
        <Form.Item
          name="macroSplit"
          label={t<string>("mealPlanForm.macroSplit")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Select options={macroSplits} placeholder={t("common:selectPlaceholder")} onChange={onMacroSplitChange} />
        </Form.Item>
        <Form.Item
          name="mealsSchema"
          label={t<string>("mealPlanForm.mealsPerDay")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Select options={mealsPerDay} placeholder={t("common:selectPlaceholder")} />
        </Form.Item>

        <NutritionGenerationCaloriesField />

        {activeMealsPlanId && (
          <Form.Item label="Kopiuj suplementacje z obecnej diety" valuePropName="checked" name="copySupplementsPlan">
            <Switch />
          </Form.Item>
        )}

        <Row className="my-4 justify-center">
          <Col span={12}>
            <Typography.Text strong>{t("nutrients.protein")}</Typography.Text>
            <Slider min={0} max={100} value={protein} onChange={(value) => update("protein", value)} />
          </Col>
          <Col span={5}>
            <InputNumber
              addonAfter={
                <div className="w-[40px]">{`${countNutrientGrams(
                  protein,
                  targetCalories ?? 0,
                  KCAL_PER_NUTRIENT_GRAM.protein,
                )} g`}</div>
              }
              min={0}
              max={100}
              className="ml-1 mt-4"
              value={protein}
              formatter={(value) => `${value}%`}
              parser={(value) => (value !== undefined ? Number(value.replace("%", "")) : 0)}
              onChange={(value) => update("protein", value)}
            />
          </Col>
          <Col span={12}>
            <Typography.Text strong>{t("nutrients.carbohydrates")}</Typography.Text>
            <Slider min={0} max={100} value={carbs} onChange={(value) => update("carbs", value)} />
          </Col>
          <Col span={5}>
            <InputNumber
              className="ml-1 mt-4"
              addonAfter={
                <div className="w-[40px]">{`${countNutrientGrams(
                  carbs,
                  targetCalories ?? 0,
                  KCAL_PER_NUTRIENT_GRAM.carbs,
                )} g`}</div>
              }
              formatter={(value) => `${value}%`}
              parser={(value) => (value !== undefined ? Number(value.replace("%", "")) : 0)}
              min={0}
              max={100}
              value={carbs}
              onChange={(value) => update("carbs", value)}
            />
          </Col>
          <Col span={12}>
            <Typography.Text strong>{t("nutrients.fat")}</Typography.Text>
            <Slider min={0} max={100} value={fat} onChange={(value) => update("fat", value)} />
          </Col>
          <Col span={5}>
            <InputNumber
              className="ml-1 mt-4"
              addonAfter={
                <div className="w-[40px]">{`${countNutrientGrams(
                  fat,
                  targetCalories ?? 0,
                  KCAL_PER_NUTRIENT_GRAM.fat,
                )} g`}</div>
              }
              formatter={(value) => `${value}%`}
              parser={(value) => (value !== undefined ? Number(value.replace("%", "")) : 0)}
              min={0}
              max={100}
              value={fat}
              onChange={(value) => update("fat", value)}
            />
          </Col>
        </Row>
      </Form>
    </>
  );
});

TraineeNutritionGeneratorForm.displayName = "TraineeNutritionGeneratorForm";

export default TraineeNutritionGeneratorForm;
